(() => {
  const INCH_TO_CENTIMETER = 2.54;
  const POUND_TO_KILOGRAM = 0.45359237;

  class ProductDetailsDescription extends HTMLElement {
    constructor() {
      super();

      this.handleClick = this.handleClick.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.handleWindowLoad = this.handleWindowLoad.bind(this);
      this.handleSectionLoad = this.handleSectionLoad.bind(this);
      this.variantEventName = null;
      this.variantMeasurements = null;
      this.dimensionUnit = "in";
      this.weightUnit = "lb";
    }

    connectedCallback() {
      if (this.dataset.initialized === "true") {
        return;
      }

      this.dataset.initialized = "true";
      this.variantMeasurements = this.getVariantMeasurements();
      this.dimensionUnit = this.querySelector('[data-unit-button="dimension"][aria-pressed="true"]')?.dataset.unit || this.dimensionUnit;
      this.weightUnit = this.querySelector('[data-unit-button="weight"][aria-pressed="true"]')?.dataset.unit || this.weightUnit;

      this.addEventListener("click", this.handleClick);
      document.addEventListener("shopify:section:load", this.handleSectionLoad);
      this.connectVariantListener();

      if (this.variantMeasurements) {
        this.updateVariant(this.variantMeasurements.selectedVariantId);
      } else {
        this.updateDimensions(this.dimensionUnit, false);
        this.updateWeight(this.weightUnit, false);
      }
    }

    disconnectedCallback() {
      this.removeEventListener("click", this.handleClick);
      document.removeEventListener("shopify:section:load", this.handleSectionLoad);
      window.removeEventListener("load", this.handleWindowLoad);
      this.disconnectVariantListener();
      delete this.dataset.initialized;
    }

    handleClick(event) {
      const button = event.target.closest("[data-unit-button]");

      if (!button || !this.contains(button) || button.getAttribute("aria-pressed") === "true") {
        return;
      }

      const control = button.dataset.unitButton;
      const unit = button.dataset.unit;

      this.updatePressedState(control, button);

      if (control === "dimension") {
        this.dimensionUnit = unit;
        this.updateDimensions(unit, true);
      } else if (control === "weight") {
        this.weightUnit = unit;
        this.updateWeight(unit, true);
      }
    }

    handleVariantChange({ variantId, variant } = {}) {
      this.updateVariant(variantId || variant?.id);
    }

    handleWindowLoad() {
      this.connectVariantListener();
    }

    handleSectionLoad() {
      this.connectVariantListener();
    }

    connectVariantListener() {
      const eventBus = window.themeCore?.EventBus;
      const productSection = this.getProductSection();

      if (!eventBus || !productSection) {
        window.addEventListener("load", this.handleWindowLoad, { once: true });
        return;
      }

      const eventName = `pdp:section-${productSection.dataset.sectionId}:change-variant`;

      if (this.variantEventName === eventName) {
        return;
      }

      this.disconnectVariantListener();
      eventBus.listen(eventName, this.handleVariantChange);
      this.variantEventName = eventName;
    }

    disconnectVariantListener() {
      const eventBus = window.themeCore?.EventBus;

      if (eventBus && this.variantEventName) {
        eventBus.remove(this.variantEventName, this.handleVariantChange);
      }

      this.variantEventName = null;
    }

    getProductSection() {
      const productSections = [...document.querySelectorAll('[data-section-type="product"][data-section-id]')];

      return productSections.find((section) => section.dataset.productHandle === this.dataset.productHandle) || productSections[0];
    }

    getVariantMeasurements() {
      const dataElement = this.querySelector("[data-variant-measurements]");

      if (!dataElement) {
        return null;
      }

      try {
        return JSON.parse(dataElement.textContent);
      } catch (error) {
        return null;
      }
    }

    updateVariant(variantId) {
      if (!this.variantMeasurements || !variantId) {
        return;
      }

      const variant = this.variantMeasurements.variants[String(variantId)] || {};
      const fallback = this.variantMeasurements.fallback || {};
      const diameterValue = this.getCanonicalValue(variant.diameter, fallback.diameter);
      const isRound = diameterValue !== null;

      this.querySelectorAll("[data-dimension]").forEach((measurement) => {
        const key = measurement.dataset.measurementKey;
        const value = this.getCanonicalValue(variant[key], fallback[key]);
        const isRectangularOnly = key === "width" || key === "height";

        measurement.hidden = value === null || (isRound && isRectangularOnly);
        measurement.dataset.canonicalValue = value === null ? "" : String(value);
      });

      const dimensionsGroup = this.querySelector("[data-dimensions-group]");
      const dimensions = [...this.querySelectorAll("[data-dimension]")];
      const visibleDimensions = dimensions.filter((measurement) => !measurement.hidden);
      const dimensionsList = this.querySelector("[data-dimension-values]");
      const hasDimensions = visibleDimensions.length > 0;

      if (dimensionsList) {
        dimensionsList.toggleAttribute("data-round-dimensions", isRound);

        if (isRound) {
          dimensionsList.dataset.visibleDimensions = String(visibleDimensions.length);
        } else {
          delete dimensionsList.dataset.visibleDimensions;
        }
      }

      if (dimensionsGroup) {
        dimensionsGroup.hidden = !hasDimensions;
      }

      const weight = this.querySelector("[data-weight]");
      const weightGroup = this.querySelector("[data-weight-group]");
      const weightValue = this.getCanonicalValue(variant.weight, fallback.weight);
      const hasWeight = Boolean(weight) && weightValue !== null;

      if (weight) {
        weight.dataset.canonicalValue = hasWeight ? String(weightValue) : "";
      }

      if (weightGroup) {
        weightGroup.hidden = !hasWeight;
      }

      this.updateLayout(hasDimensions || hasWeight);
      this.updateDimensions(this.dimensionUnit, false);
      this.updateWeight(this.weightUnit, false);
    }

    getCanonicalValue(variantValue, fallbackValue) {
      const sourceValue = variantValue === null || variantValue === undefined || variantValue === "" ? fallbackValue : variantValue;
      const number = Number(sourceValue);

      return sourceValue === null || sourceValue === undefined || sourceValue === "" || !Number.isFinite(number) ? null : number;
    }

    updateLayout(hasSpecs) {
      const specs = this.querySelector("[data-specs]");
      const card = this.querySelector("[data-details-card]");

      if (specs) {
        specs.hidden = !hasSpecs;
      }

      if (card) {
        card.classList.toggle("has-specs", hasSpecs);
      }
    }

    updatePressedState(control, activeButton) {
      this.querySelectorAll(`[data-unit-button="${control}"]`).forEach((button) => {
        const isActive = button === activeButton;
        button.classList.toggle("is-active", isActive);
        button.setAttribute("aria-pressed", isActive.toString());
      });
    }

    updateDimensions(unit, announce) {
      const precision = this.getPrecision("dimensionPrecision");
      const unitLabel = unit === "cm" ? "cm" : "in";

      this.querySelectorAll("[data-dimension]").forEach((measurement) => {
        if (measurement.hidden) {
          return;
        }

        const canonicalValue = Number(measurement.dataset.canonicalValue);

        if (!Number.isFinite(canonicalValue)) {
          return;
        }

        const displayValue = unit === "cm" ? canonicalValue * INCH_TO_CENTIMETER : canonicalValue;

        this.updateMeasurement(measurement, displayValue, unitLabel, precision);
      });

      if (announce) {
        this.announce(unit === "cm" ? "Dimensions shown in centimeters" : "Dimensions shown in inches");
      }
    }

    updateWeight(unit, announce) {
      const weight = this.querySelector("[data-weight]");
      const weightGroup = this.querySelector("[data-weight-group]");

      if (!weight || weightGroup?.hidden) {
        return;
      }

      const precision = this.getPrecision("weightPrecision");
      const canonicalValue = Number(weight.dataset.canonicalValue);

      if (!Number.isFinite(canonicalValue)) {
        return;
      }

      const isKilograms = unit === "kg";
      const displayValue = isKilograms ? canonicalValue * POUND_TO_KILOGRAM : canonicalValue;
      const unitLabel = isKilograms ? "kg" : "lbs";
      const iconUnit = weight.querySelector("[data-weight-icon-unit]");

      this.updateMeasurement(weight, displayValue, unitLabel, precision);

      if (iconUnit) {
        iconUnit.textContent = unitLabel;
      }

      if (announce) {
        this.announce(isKilograms ? "Weight shown in kilograms" : "Weight shown in pounds");
      }
    }

    updateMeasurement(container, value, unit, precision) {
      const valueElement = container.querySelector("[data-measurement-value]");
      const unitElement = container.querySelector("[data-measurement-unit]");

      if (valueElement) {
        valueElement.textContent = this.formatNumber(value, precision);
      }

      if (unitElement) {
        unitElement.textContent = unit;
      }
    }

    formatNumber(value, precision) {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        maximumFractionDigits: precision
      }).format(value);
    }

    getPrecision(datasetKey) {
      const value = Number(this.dataset[datasetKey]);

      return Number.isInteger(value) ? value : 1;
    }

    announce(message) {
      const announcement = this.querySelector("[data-unit-announcement]");

      if (announcement) {
        announcement.textContent = message;
      }
    }
  }

  if (!customElements.get("product-details-description")) {
    customElements.define("product-details-description", ProductDetailsDescription);
  }
})();
